import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { MondaySourceSettingsApi } from "./MondaySourceSettings.Api";
import type { MondaySourceSettingsViewModel } from "./MondaySourceSettings.ViewModel.types";
import type {
  MondayBoard,
  MondayColumn,
  MondayConfiguration,
} from "./MondaySourceSettings.types";

const NONE_OPTION = "__none";

const normalizeId = (value: unknown): string => String(value ?? "");

const resolveDefaultStatuses = (
  statusColumn: MondayColumn | undefined,
  doneStatus: string | undefined,
): string[] => {
  const statuses = statusColumn?.statuses ?? [];
  return statuses.filter((s) => s !== "Done" && s !== doneStatus);
};

const resolveDefaultDoneStatus = (
  statusColumn: MondayColumn | undefined,
): string | undefined => {
  const statuses = statusColumn?.statuses ?? [];
  const explicitDone = statuses.find((s) => s === "Done");
  return explicitDone ?? (statuses.length > 0 ? statuses[statuses.length - 1] : undefined);
};

/** People columns whose title means the item's assignee rather than, say, its reporter. */
const ASSIGNEE_COLUMN_TITLE = /^(owner|assignee|assigned( to)?|person|people)$/i;

/**
 * "Assigned To Me" filters on the owner column, so the default must be the column that means
 * the assignee. The board's first people column can be "Reporter", which hid every item on a
 * new connection.
 */
const resolveDefaultOwnerColumn = (
  peopleColumns: MondayColumn[] | undefined,
): MondayColumn | undefined =>
  peopleColumns?.find((column) => ASSIGNEE_COLUMN_TITLE.test((column.title ?? "").trim())) ??
  peopleColumns?.[0];

export const useMondaySourceSettingsViewModel = (
  model: SourceSettingsModel,
): MondaySourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const autoSelectedBoardForConnectionRef = useRef<string | null>(null);
  const seededDefaultsForColumnRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [boards, setBoards] = useState<MondayBoard[]>([]);
  const [boardsLoaded, setBoardsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.Monday && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<MondayConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const configurationRef = useRef(configuration);
  useEffect(() => {
    configurationRef.current = configuration;
  }, [configuration]);

  const selectedBoardId = normalizeId(configuration.board?.id);
  const selectedStatusColumnId = normalizeId(configuration.statusColumn?.id);

  // Initialise defaults on first render for this connection
  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      autoSelectedBoardForConnectionRef.current = null;
      seededDefaultsForColumnRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<MondayConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        statuses: cfg.statuses ?? [],
        doneStatus: cfg.doneStatus ?? undefined,
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  // Reset on profile/auth change
  useEffect(() => {
    const currentProfileIdentity = `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setBoards([]);
    autoSelectedBoardForConnectionRef.current = null;
    seededDefaultsForColumnRef.current = null;

    updateConnectionConfiguration<MondayConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        board: undefined,
        statusColumn: undefined,
        statuses: [],
        doneStatus: undefined,
        ownerColumn: undefined,
        dueDateColumn: undefined,
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  // Load boards
  useEffect(() => {
    const loadBoards = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setBoards([]);
        setBoardsLoaded(true);
        return;
      }

      const mondayApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<MondaySourceSettingsApi>(
          SourceTypeIds.Monday,
        );
      if (!mondayApi) {
        setBoards([]);
        setBoardsLoaded(true);
        return;
      }

      setBoardsLoaded(false);
      setHasError(false);
      try {
        const nextBoards = await mondayApi.getBoards(model.boardId, editingConnectionId);
        setBoards(nextBoards);

        if (
          !selectedBoardId &&
          nextBoards.length > 0 &&
          autoSelectedBoardForConnectionRef.current !== editingConnectionId
        ) {
          autoSelectedBoardForConnectionRef.current = editingConnectionId;
          const firstBoard = nextBoards[0];
          applyBoardDefaults(firstBoard, { isDefault: true });
        }
      } catch {
        setBoards([]);
        setHasError(true);
      } finally {
        setBoardsLoaded(true);
      }
    };

    void loadBoards();
  }, [editingConnectionId, isVisible, model.boardId, selectedBoardId]);

  // Seed column defaults once per statusColumn selection
  useEffect(() => {
    if (!isVisible || !selectedBoardId || !selectedStatusColumnId) {
      return;
    }

    const selectionKey = `${editingConnectionId}:${selectedBoardId}:${selectedStatusColumnId}`;
    if (seededDefaultsForColumnRef.current === selectionKey) {
      return;
    }

    const cfg = configurationRef.current;
    const hasStatuses = (cfg.statuses ?? []).length > 0;
    const hasDoneStatus = Boolean(cfg.doneStatus);

    if (hasStatuses && hasDoneStatus) {
      seededDefaultsForColumnRef.current = selectionKey;
      return;
    }

    seededDefaultsForColumnRef.current = selectionKey;
    const statusColumn = cfg.statusColumn;
    const doneStatus = cfg.doneStatus ?? resolveDefaultDoneStatus(statusColumn);
    const statuses =
      (cfg.statuses ?? []).length > 0
        ? cfg.statuses!
        : resolveDefaultStatuses(statusColumn, doneStatus);

    updateConnectionConfiguration<MondayConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (existing) =>
        normalizeId(existing.board?.id) !== selectedBoardId ||
        normalizeId(existing.statusColumn?.id) !== selectedStatusColumnId
          ? existing
          : {
              ...existing,
              doneStatus: existing.doneStatus ?? doneStatus,
              statuses: (existing.statuses ?? []).length > 0 ? existing.statuses : statuses,
            },
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible, selectedBoardId, selectedStatusColumnId]);

  // ─── Derived values ────────────────────────────────────────────────────────

  const selectedBoard = useMemo(
    () => boards.find((b) => normalizeId(b.id) === selectedBoardId),
    [boards, selectedBoardId],
  );

  const statusColumns = useMemo(
    () => configuration.board?.statusColumns ?? selectedBoard?.statusColumns ?? [],
    [configuration.board, selectedBoard],
  );

  const dateColumns = useMemo(
    () => configuration.board?.dateColumns ?? selectedBoard?.dateColumns ?? [],
    [configuration.board, selectedBoard],
  );

  const peopleColumns = useMemo(
    () => configuration.board?.peopleColumns ?? selectedBoard?.peopleColumns ?? [],
    [configuration.board, selectedBoard],
  );

  const selectedStatusColumn = useMemo(
    () => statusColumns.find((c) => normalizeId(c.id) === selectedStatusColumnId),
    [statusColumns, selectedStatusColumnId],
  );

  const statuses = useMemo(
    () => selectedStatusColumn?.statuses ?? configuration.statusColumn?.statuses ?? [],
    [selectedStatusColumn, configuration.statusColumn],
  );

  const selectedOwnerColumnId = normalizeId(configuration.ownerColumn?.id);
  const selectedDueDateColumnId = normalizeId(configuration.dueDateColumn?.id);

  // ─── Helpers ───────────────────────────────────────────────────────────────

  /**
   * Selects a board along with the column and status defaults it implies.
   *
   * @param board The board to select.
   * @param options `isDefault` when the editor picks the board itself rather than the user. The
   *   board is then only filled in where none is set, so it also applies to the saved settings.
   */
  function applyBoardDefaults(
    board: MondayBoard,
    options: { isDefault?: boolean } = {},
  ): void {
    const defaultStatusColumn =
      board.defaultStatusColumn ??
      (board.statusColumns && board.statusColumns.length > 0
        ? board.statusColumns[0]
        : undefined);
    const doneStatus = resolveDefaultDoneStatus(defaultStatusColumn);
    const defaultStatuses = resolveDefaultStatuses(defaultStatusColumn, doneStatus);
    const defaultPeopleColumn = resolveDefaultOwnerColumn(board.peopleColumns);

    updateConnectionConfiguration<MondayConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) =>
        options.isDefault && normalizeId(cfg.board?.id)
          ? cfg
          : {
              ...cfg,
              board,
              statusColumn:
                cfg.statusColumn && board.statusColumns?.some((c) => c.id === cfg.statusColumn?.id)
                  ? cfg.statusColumn
                  : defaultStatusColumn,
              statuses: (cfg.statuses ?? []).length > 0 ? cfg.statuses : defaultStatuses,
              doneStatus: cfg.doneStatus ?? doneStatus,
              ownerColumn:
                cfg.ownerColumn && board.peopleColumns?.some((c) => c.id === cfg.ownerColumn?.id)
                  ? cfg.ownerColumn
                  : defaultPeopleColumn,
              dueDateColumn: cfg.dueDateColumn ?? undefined,
            },
      options,
    );
  }

  return {
    isVisible,
    boards,
    boardsLoaded,
    hasError,
    selectedBoardId: selectedBoardId || NONE_OPTION,
    selectedBoardName:
      selectedBoardId === NONE_OPTION
        ? ""
        : (boards.find((b) => normalizeId(b.id) === selectedBoardId)?.name ?? ""),
    statusColumns,
    selectedStatusColumnId: selectedStatusColumnId || NONE_OPTION,
    selectedStatusColumnTitle:
      selectedStatusColumnId === NONE_OPTION
        ? ""
        : (statusColumns.find((c) => normalizeId(c.id) === selectedStatusColumnId)?.title ?? ""),
    statuses,
    selectedStatuses: configuration.statuses ?? [],
    selectedDoneStatus: configuration.doneStatus ?? NONE_OPTION,
    assignedToMe: configuration.assignedToMe ?? false,
    peopleColumns,
    selectedOwnerColumnId: selectedOwnerColumnId || NONE_OPTION,
    selectedOwnerColumnTitle:
      selectedOwnerColumnId === NONE_OPTION
        ? ""
        : (peopleColumns.find((c) => normalizeId(c.id) === selectedOwnerColumnId)?.title ?? ""),
    dateColumns,
    selectedDueDateColumnId: selectedDueDateColumnId || NONE_OPTION,
    selectedDueDateColumnTitle:
      selectedDueDateColumnId === NONE_OPTION
        ? ""
        : (dateColumns.find((c) => normalizeId(c.id) === selectedDueDateColumnId)?.title ?? ""),
    noneOptionValue: NONE_OPTION,

    onSelectBoard: (boardId?: string) => {
      const selectedId = String(boardId || NONE_OPTION);
      const board = boards.find((b) => normalizeId(b.id) === selectedId);

      if (selectedId === NONE_OPTION || !board) {
        updateConnectionConfiguration<MondayConfiguration>(
          editingConnectionRef.current,
          onUpdateRef.current,
          (cfg) => ({
            ...cfg,
            board: undefined,
            statusColumn: undefined,
            statuses: [],
            doneStatus: undefined,
            ownerColumn: undefined,
            dueDateColumn: undefined,
          }),
        );
        seededDefaultsForColumnRef.current = null;
        return;
      }

      seededDefaultsForColumnRef.current = null;
      applyBoardDefaults(board);
    },

    onSelectStatusColumn: (columnId?: string) => {
      const selectedId = String(columnId || NONE_OPTION);
      const column = statusColumns.find((c) => normalizeId(c.id) === selectedId);

      seededDefaultsForColumnRef.current = null;
      const doneStatus = resolveDefaultDoneStatus(column);
      const defaultStatuses = resolveDefaultStatuses(column, doneStatus);

      updateConnectionConfiguration<MondayConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          statusColumn: selectedId === NONE_OPTION ? undefined : column,
          statuses: defaultStatuses,
          doneStatus,
        }),
      );
    },

    onSelectStatuses: (selectedStatuses: string[]) => {
      updateConnectionConfiguration<MondayConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({ ...cfg, statuses: selectedStatuses }),
      );
    },

    onSelectDoneStatus: (status?: string) => {
      updateConnectionConfiguration<MondayConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          doneStatus: status === NONE_OPTION ? undefined : status,
        }),
      );
    },

    onToggleAssignedToMe: (value: boolean) => {
      updateConnectionConfiguration<MondayConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({ ...cfg, assignedToMe: value }),
      );
    },

    onSelectOwnerColumn: (columnId?: string) => {
      const selectedId = String(columnId || NONE_OPTION);
      const column = peopleColumns.find((c) => normalizeId(c.id) === selectedId);

      updateConnectionConfiguration<MondayConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          ownerColumn: selectedId === NONE_OPTION ? undefined : column,
        }),
      );
    },

    onSelectDueDateColumn: (columnId?: string) => {
      const selectedId = String(columnId || NONE_OPTION);
      const column = dateColumns.find((c) => normalizeId(c.id) === selectedId);

      updateConnectionConfiguration<MondayConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          dueDateColumn: selectedId === NONE_OPTION ? undefined : column,
        }),
      );
    },
  };
};
